import React, { useEffect, useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'

import { Divider, Flex, SelectInput, Td, Tr } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import { CardStats } from '@components/CardStatus'
import { DashboardTable } from '@components/DashboardTable'
import { DownloadReportButton } from '@components/DownloadReportButton'
import PageTitle from '@components/PageTitle'
import Spinner from '@components/Spinner'
import Template from '@components/Template'
import '../../styles/index.css'
import { dashboardApi } from '@services/Dashboard'
import { disciplineApi } from '@services/DisciplineApi'
import { filtersApi } from '@services/filtersApi'
import { formatOptions } from '@utils/helpers/formatOptions'
import Formatters from '@utils/helpers/Formatters'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Filler,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Filler,
	Legend
)

const styles = {
	divider: 'mt-[32px] mb-[12px]',
}

export default function App() {
	const currentDate = new Date()
	const previousMonth = currentDate.getMonth() + 1
	const actualYear = currentDate.getFullYear()

	const [yearFilter, setYearFilter] = useState<string | number>(actualYear)
	const [monthFilter, setMonthFilter] = useState<string | number>(previousMonth)
	const [selectedDiscipline, setSelectedDiscipline] = useState<string | number>(
		''
	)
	const [selectedJury, setSelectedJury] = useState<string | number>('')
	const [selectedYearFilter, setSelectedYearFilter] = useState<string | number>(
		''
	)

	const { data: filtersData } = filtersApi.useListQuery({})

	const { data: disciplinesData } = disciplineApi.useListQuery({
		page: 1,
		limit: 100,
	})

	const {
		data,
		isLoading: isLoadingDashboard,
		refetch,
	} = dashboardApi.useListQuery({
		year: yearFilter,
		month: monthFilter,
	})

	const disciplineOptions = useMemo(() => {
		const sortedData = disciplinesData?.data
			? [...disciplinesData.data].sort((a, b) => a.name.localeCompare(b.name))
			: undefined

		return formatOptions(sortedData, 'Selecione a Disciplina')
	}, [disciplinesData])

	const juryOptions = useMemo(() => {
		const sortedData = filtersData?.juries
			? [...filtersData.juries].sort((a, b) => a.name.localeCompare(b.name))
			: undefined

		return formatOptions(sortedData, 'Todas as Bancas')
	}, [filtersData])

	const yearOptions = useMemo(() => {
		const sortedData = filtersData?.years
			? [...filtersData.years].sort((a, b) => Number(b.name) - Number(a.name))
			: undefined

		return formatOptions(sortedData, 'Todos os Anos')
	}, [filtersData])

	const { data: recurrentArticles, isLoading: isLoadingRecurrent } =
		dashboardApi.useGetRecurrentQuestionsQuery(
			{
				disciplineId: selectedDiscipline,
				yearId: selectedYearFilter,
				juryId: selectedJury,
			},
			{
				skip: !selectedDiscipline,
			}
		)

	const isLoading = isLoadingDashboard

	const monthOptions = [
		{ option: 'Selecione um mês', value: '' },
		{ option: 'Janeiro', value: '1' },
		{ option: 'Fevereiro', value: '2' },
		{ option: 'Março', value: '3' },
		{ option: 'Abril', value: '4' },
		{ option: 'Maio', value: '5' },
		{ option: 'Junho', value: '6' },
		{ option: 'Julho', value: '7' },
		{ option: 'Agosto', value: '8' },
		{ option: 'Setembro', value: '9' },
		{ option: 'Outubro', value: '10' },
		{ option: 'Novembro', value: '11' },
		{ option: 'Dezembro', value: '12' },
	]

	const chartOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false,
			},
		},
		height: 500,
		scales: {
			y: {
				beginAtZero: true,
			},
			x: {
				beginAtZero: true,
			},
		},
	}

	const labels = data?.clientsTotalPerMonth?.map((client) => {
		return monthFilter ? Formatters.createdAt(client.date, 6) : client.date

		// const date = new Date(client.date)
		// const day = date.getDate()
		// const month = date.getMonth() + 1
		// const formattedDate = `${day < 10 ? '0' : ''}${day}/${
		// 	month < 10 ? '0' : ''
		// }${month}`

		// return formattedDate
	})

	const dataT = {
		labels,
		datasets: [
			{
				label: 'Alunos por periodo',
				data: data?.clientsTotalPerMonth?.map((client) => client.total),
				borderColor: '#6BBBDC',
				backgroundColor: 'rgba(82, 176, 238, 0.166)',
				tension: 0.1,
				fill: true,
			},
		],
	}

	const getLabel = (
		opts: Array<{
			value: string | number
			label?: string
			option?: string
			name?: string
		}>,
		value: string | number
	) => {
		if (!opts || value === '' || value === undefined || value === null)
			return ''

		const found = opts.find((opt) => String(opt.value) === String(value))

		if (!found) return ''

		return found.label || found.option || found.name || ''
	}

	const selectedDisciplineLabel = getLabel(
		disciplineOptions,
		selectedDiscipline
	)
	const selectedJuryLabel = getLabel(juryOptions, selectedJury)
	const selectedYearLabel = getLabel(yearOptions, selectedYearFilter)

	useEffect(() => {
		refetch()
	}, [refetch, yearFilter, monthFilter])

	return (
		<div>
			<Template>
				<Breadcrumb />
				<PageTitle title='Dashboard' />
				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<>
						<div className='grid grid-cols-4 gap-x-8'>
							<CardStats
								value={data?.clientsTotal}
								key={1}
								title='Alunos'
								icon='team'
							/>
							<CardStats
								value={data?.activePaidClientsTotal}
								key={1}
								title='Alunos pagos ativos'
								icon='teamCash'
							/>
							<CardStats
								value={data?.classesTotal}
								key={1}
								title='Turmas'
								icon='class'
							/>
							<CardStats
								value={data?.questionsTotal}
								key={1}
								title='Questões cadastradas'
								icon='question'
							/>
						</div>
						<div className='grid grid-cols-2 gap-x-8 mt-8 mb-8'>
							<DashboardTable
								title='Artigos mais recorrentes'
								columns={[{ label: 'Artigo' }, { label: 'Quantidade' }]}
								data={recurrentArticles}
								isLoading={isLoadingRecurrent}
								action={
									selectedDiscipline &&
									recurrentArticles &&
									recurrentArticles.length > 0 ? (
										<DownloadReportButton
											disciplineId={selectedDiscipline}
											juryId={selectedJury}
											yearId={selectedYearFilter}
											disciplineName={
												getLabel(disciplineOptions, selectedDiscipline) ||
												'Disciplina'
											}
											juryName={
												getLabel(juryOptions, selectedJury) || 'Todas as Bancas'
											}
											year={
												getLabel(yearOptions, selectedYearFilter) ||
												'Todos os Anos'
											}
										/>
									) : null
								}
								className='mb-8'
								emptyMessage={
									!selectedDiscipline
										? 'Selecione uma disciplina para ver os dados.'
										: 'Nenhum dado encontrado'
								}
								filters={
									<>
										<div title={selectedDisciplineLabel} className='w-full'>
											<SelectInput
												isSearchable
												btnClass='w-full h-auto min-h-[42px] py-[8px] px-[13px] border border-gray-300 rounded flex justify-between items-center whitespace-normal text-left'
												options={disciplineOptions}
												selectedValue={selectedDiscipline}
												onChange={(e) => setSelectedDiscipline(e.value)}
												multiline
											/>
										</div>

										<div title={selectedJuryLabel} className='w-full'>
											<SelectInput
												isSearchable
												btnClass='w-full h-auto min-h-[42px] py-[8px] px-[13px] border border-gray-300 rounded flex justify-between items-center whitespace-normal text-left'
												options={juryOptions}
												selectedValue={selectedJury}
												onChange={(e) => setSelectedJury(e.value)}
												multiline
											/>
										</div>

										<div title={selectedYearLabel} className='w-full'>
											<SelectInput
												btnClass='w-full py-[8px] px-[13px] border border-gray-300 rounded flex justify-between items-center'
												options={yearOptions}
												selectedValue={selectedYearFilter}
												onChange={(e) => setSelectedYearFilter(e.value)}
											/>
										</div>
									</>
								}
								renderRow={(item, index) => (
									<Tr key={index}>
										<Td className='px-[12px] py-[10px] w-[70%]'>
											<Flex className='items-center gap-4 font-medium text-gray-800'>
												{item.article}
											</Flex>
										</Td>
										<Td className='w-[30%] text-end pr-[18px] font-bold text-primary-light'>
											{item.count}
										</Td>
									</Tr>
								)}
							/>

							<DashboardTable
								title='Turmas mais vendidas'
								columns={[
									{ label: 'id' },
									{ label: 'turma' },
									{ label: 'quantidade de alunos' },
								]}
								data={data?.mostSoldClasses}
								isLoading={false}
								renderRow={(item) => (
									<Tr key={item.id}>
										<Td className='px-[12px] py-[10px]'>{item.id}</Td>
										<Td className='w-[60%] pl-3'>
											<Flex className='items-center gap-4'>{item.name}</Flex>
										</Td>
										<Td className='w-[35%] text-end pr-[18px]'>{item.total}</Td>
									</Tr>
								)}
							/>

							<DashboardTable
								title='Últimos alunos cadastrados'
								columns={[{ label: 'nome' }, { label: 'data do cadastrado' }]}
								data={data?.lastClients}
								isLoading={false}
								renderRow={(item) => (
									<Tr key={item.id}>
										<Td className='w-[68%] px-[12px] py-[10px]'>
											<Flex className='items-center gap-4'>{item.name}</Flex>
										</Td>
										<Td className='text-end pr-[16px]'>
											{Formatters.createdAt(item.createdAt)}
										</Td>
									</Tr>
								)}
							/>
						</div>

						{data?.clientsTotalPerMonth &&
							data?.clientsTotalPerMonth?.length > 0 && (
								<div className='w-full bg-white border-[1px] border-gray-250 rounded-xl px-[39px] py-[32px]'>
									<div className='flex justify-between pb-[43px]'>
										<p className='font-600 text-base text-gray-800'>
											Alunos por período
										</p>
										<p className='flex gap-[5px] font-600 text-base text-gray-800 items-center'>
											<span className='w-4 h-4 rounded-[2px] border-[0.2px] border-gray-950 bg-primary-light' />{' '}
											Número de alunos por período
										</p>
										<SelectInput
											btnClass='py-[8px] px-[13px] w-[100px]'
											options={data.yearInterval.slice().reverse()}
											onChange={(e) => setYearFilter(e.value)}
										/>
										<SelectInput
											btnClass='py-[8px] px-[13px] w-[158px]'
											options={monthOptions}
											onChange={(e) => setMonthFilter(e.value)}
										/>
									</div>
									<Line options={chartOptions} data={dataT} />
								</div>
							)}
					</>
				)}
				<Divider className={styles.divider} />
			</Template>
		</div>
	)
}
